import { Injectable } from '@angular/core';
import { ToastComponent } from '../components/toast/toast.component';
import { Toast, ToastListType, ToastPosition, ToastSeverity, ToastType } from '../interfaces/toast.interface';

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    private toastComponentRef!: ToastComponent;

    constructor() {}

    public registerToastComponent(toastComponent: ToastComponent) {
        this.toastComponentRef = toastComponent;
    }

    /**
     * Show a toast message
     * @param {string} message - The text to show in the toast. It can be a translation key
     * @param {"success" | "error" | "warning" | "info"} severity - The severity of toast to show
     * @param {Object} args - Arguments to be used in the translation
     * @param {number} duration - Duration in milliseconds. 0 means it will not disappear automatically
     * @param {ToastPosition} position - Position of the toast. It can be 'center' or 'right'
     * @param {Function} clickHandler - Action on click
     * @param {"none" | "ordered" | "unordered"} listType - Type of list when listArguments are specified
     * @param {string[]} listArguments - Elements to display in the list
     * @param {"simple" | "actionable"} type - Type of toast (simple = basic toast, actionable = toast with an action button)
     * @param {string} actionButtonText - Text to display in the action button (if type=actionable)
     * @param {boolean} ignoreRemoval - Toast will be ignored from toastService.removeAll() calls unless "force" is set to true
     * @param {boolean} allowDuplicates - Toast will be displayed even if there is an already active toast with the same parameters
     */
    public showToast(
        message: string,
        severity?: ToastSeverity,
        args?: { [key: string]: string | number | undefined },
        duration?: number,
        position?: ToastPosition,
        clickHandler?: () => void,
        listType?: ToastListType,
        listArguments?: string[],
        type?: ToastType,
        actionButtonText?: string,
        ignoreRemoval?: boolean,
        allowDuplicates?: boolean,
    ): number;
    public showToast(toast: Toast): number;

    public showToast(
        messageOrToast: string | Toast,
        severity: ToastSeverity = 'info',
        args?: { [key: string]: string | number | undefined },
        duration?: number,
        position?: ToastPosition,
        clickHandler?: () => void,
        listType: ToastListType = 'none',
        listArguments: string[] = [],
        type: ToastType = 'simple',
        actionButtonText?: string,
        ignoreRemoval?: boolean,
        allowDuplicates?: boolean,
    ): number {
        if (typeof messageOrToast === 'string') {
            const toast: Toast = {
                body: messageOrToast,
                severity,
                args,
                duration,
                position,
                clickHandler,
                listType,
                listArguments,
                type,
                actionButtonText,
                ignoreRemoval: ignoreRemoval,
                allowDuplicates: allowDuplicates,
            };

            return this.toastComponentRef.showToast(toast);
        } else {
            return this.toastComponentRef.showToast(messageOrToast);
        }
    }

    public removeToast(toastId: number) {
        this.toastComponentRef.removeToast(toastId);
    }

    public removeAll(force: boolean = false) {
        this.toastComponentRef.removeAll(force);
    }
}
